import { UploadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Modal,
  Spin,
  Upload,
  List,
  Typography,
  Alert,
  Collapse,
  Tag,
  Space,
} from "antd";
import { UploadProps } from "antd/es/upload";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import { inventoryService } from "~/services/inventory";
import { INVENTORY_KEYS } from "~/services/inventory/keys";

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface ImportInventorySuccess {
  id: string;
  title: string;
  medium: string;
  clientId: string;
  pieceCount: string;
  packageCount: string;
  length: number;
  width: number;
  height: number;
  clientInternalRef: string;
  artistName: string;
  packingType: string;
  value: number;
  inventoryRef: string;
  status: string;
  itemType: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  images: any[];
  containedInventories: any[];
  [key: string]: any;
}

interface ImportInventoryError {
  row: number;
  data: Record<string, string>;
  error: string;
}

interface ImportResponse {
  success: ImportInventorySuccess[];
  errors: ImportInventoryError[];
  total: number;
  created: number;
  failed: number;
}

export const ImportInventoryModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [importResults, setImportResults] = useState<ImportResponse | null>(
    null
  );
  const queryClient = useQueryClient();
  const notification = useNotification();

  const handleUpload: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
  }) => {
    // Validate file
    if (!(file instanceof File) || file.type !== "text/csv") {
      notification.error({
        message: "Invalid file type",
        description: "Please upload a valid CSV file.",
      });
      onError?.(new Error("Invalid file type"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notification.error({
        message: "File too large",
        description: "The CSV file must be smaller than 10MB.",
      });
      onError?.(new Error("File too large"));
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      // Use our custom API call with the correct response type
      const response = await inventoryService.importInventories<ImportResponse>(
        formData
      );

      const importResponse = response.data;
      setImportResults(importResponse);

      if (importResponse.created > 0) {
        notification.success({
          message: "Import completed",
          description: `${importResponse.created} out of ${importResponse.total} inventories imported successfully.`,
        });
        queryClient.invalidateQueries({
          queryKey: [INVENTORY_KEYS.INVENTORIES],
        });
      } else {
        notification.error({
          message: "Import failed",
          description: `All ${importResponse.total} items failed to import. Please check the details.`,
        });
      }

      onSuccess?.(importResponse);
    } catch (err: any) {
      notification.error({
        message: "Error importing inventories",
        description:
          err.message || "There was an error processing the CSV file.",
      });
      onError?.(err);
      setImportResults(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setImportResults(null);
  };

  return (
    <>
      <Button
        type="primary"
        onClick={() => setOpen(true)}
        disabled={isUploading}
        className="flex items-center h-11 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm space-x-2 text-base"
      >
        <UploadOutlined />
        <span>Import CSV</span>
      </Button>
      <Modal
        open={open}
        title="Import Inventories from CSV"
        onCancel={handleClose}
        footer={[
          <Button key="close" onClick={handleClose}>
            Close
          </Button>,
        ]}
        width={importResults ? 800 : 520}
      >
        <Spin spinning={isUploading}>
          {!importResults ? (
            <>
              <Upload
                accept=".csv"
                customRequest={handleUpload}
                showUploadList={false}
                disabled={isUploading}
              >
                <Button icon={<UploadOutlined />} disabled={isUploading}>
                  Click to Upload CSV
                </Button>
              </Upload>
              <div className="mt-4 text-sm text-gray-500">
                <p>Please upload a CSV file with the correct format.</p>
                <p>Maximum file size: 10MB.</p>
              </div>
            </>
          ) : (
            <div className="import-results">
              <div className="mb-4">
                <Alert
                  message={
                    <Space direction="vertical">
                      <Text strong>Import Summary</Text>
                      <div>
                        <Tag color="blue">Total: {importResults.total}</Tag>
                        <Tag color="green">
                          Created: {importResults.created}
                        </Tag>
                        <Tag color="red">Failed: {importResults.failed}</Tag>
                      </div>
                    </Space>
                  }
                  type={importResults.created > 0 ? "info" : "error"}
                  showIcon
                />
              </div>

              {importResults.success.length > 0 && (
                <div className="mb-4">
                  <Title level={5}>Successfully Imported Items</Title>
                  <Collapse>
                    {importResults.success.map((item) => (
                      <Panel
                        header={`${item.title} (${item.inventoryRef})`}
                        key={item.id}
                      >
                        <List
                          size="small"
                          dataSource={[
                            { label: "Title", value: item.title },
                            {
                              label: "Inventory Reference",
                              value: item.inventoryRef,
                            },
                            {
                              label: "Client Internal Reference",
                              value: item.clientInternalRef,
                            },
                            { label: "Artist", value: item.artistName },
                            { label: "Medium", value: item.medium },
                            { label: "Item Type", value: item.itemType },
                            { label: "Packing Type", value: item.packingType },
                            { label: "Status", value: item.status },
                            { label: "Notes", value: item.notes },
                            {
                              label: "Dimensions",
                              value: `${item.length}×${item.width}×${item.height} cm`,
                            },
                          ]}
                          renderItem={(entry) => (
                            <List.Item>
                              <Text strong>{entry.label}:</Text>{" "}
                              {entry.value || "N/A"}
                            </List.Item>
                          )}
                        />
                      </Panel>
                    ))}
                  </Collapse>
                </div>
              )}

              {importResults.errors.length > 0 && (
                <div>
                  <Title level={5}>Failed Items</Title>
                  <Collapse>
                    {importResults.errors.map((error, index) => (
                      <Panel
                        header={
                          <Text type="danger">
                            Row {error.row}:{" "}
                            {error.data.TITLE || `Item ${index + 1}`}
                          </Text>
                        }
                        key={`error-${index}`}
                      >
                        <Alert
                          message="Error"
                          description={error.error}
                          type="error"
                          showIcon
                          className="mb-3"
                        />
                        <Title level={5}>Row Data</Title>
                        <div style={{ maxHeight: "200px", overflow: "auto" }}>
                          <List
                            size="small"
                            dataSource={Object.entries(error.data)}
                            renderItem={([key, value]) => (
                              <List.Item>
                                <Text strong>{key}:</Text> {value || "N/A"}
                              </List.Item>
                            )}
                          />
                        </div>
                      </Panel>
                    ))}
                  </Collapse>
                </div>
              )}

              <div className="mt-4">
                <Button
                  onClick={() => setImportResults(null)}
                  style={{ marginRight: 8 }}
                >
                  Upload Another File
                </Button>
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </>
  );
};
